import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../Components/Buttons/Button.js";
import AppModalCard from "../../Components/shared/AppModalCard.js";
import AppModalShell from "../../Components/shared/AppModalShell.js";
import { IS_IOS_APP } from "../../config/appTarget.js";
import { ANDROID_STORE_URL, IOS_STORE_URL } from "../../lib/appStoreLinks.js";

/**
 * Soft, dismissible "nova versao" prompt shown a few seconds into a session when
 * a newer store build than this binary is available (`updateAvailable`). Unlike
 * the force-update screen this never blocks: the user can tap "Mais tarde" to
 * dismiss it for the rest of the session. It re-appears on the next cold launch
 * until they actually update (no persistence by design).
 */
const PROMPT_DELAY_MS = 10_000;

export function UpdatePromptModal({
  updateAvailable,
}: {
  updateAvailable: boolean;
}) {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const { t } = useTranslation("features");

  useEffect(() => {
    if (!updateAvailable) return;
    const timer = setTimeout(() => setVisible(true), PROMPT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [updateAvailable]);

  if (!updateAvailable || !visible || dismissed) return null;

  const storeUrl = IS_IOS_APP ? IOS_STORE_URL : ANDROID_STORE_URL;

  return (
    <AppModalShell
      onBackdropClick={() => setDismissed(true)}
      overlayClassName="bg-black/50"
      zIndexClassName="z-[9998]"
      className="px-6"
    >
      <div style={IS_IOS_APP ? { paddingTop: "env(safe-area-inset-top)" } : undefined}>
        <AppModalCard title={t("appUpdate.promptTitle")} size="sm" panelClassName="p-6 text-center">
          <h2 className="text-xl font-bold text-my-black">
            {t("appUpdate.promptTitle")}
          </h2>
          <p className="mt-3 text-my-black/80">
            {t("appUpdate.promptBody")}
          </p>
          <a
            href={storeUrl}
            target="_blank"
            rel="noreferrer"
            onClick={() => setDismissed(true)}
            className="mt-6 block rounded-full bg-main-purple px-6 py-3 text-center font-semibold text-white"
          >
            {t("appUpdate.updateNow")}
          </a>
          <Button
            type="button"
            onClick={() => setDismissed(true)}
            variant="ghost"
            fullWidth
            className="mt-3 text-sm font-medium text-my-black/60"
          >
            {t("appUpdate.later")}
          </Button>
        </AppModalCard>
      </div>
    </AppModalShell>
  );
}
